import { useEffect, useRef, useState } from 'react';

interface Step { step: number; title: string; desc: string; }

interface ProcessSectionProps {
  steps: Step[];
  title?: string;
  subtitle?: string;
}

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

export default function ProcessSection({
  steps,
  title = 'Our Process',
  subtitle = 'A refined, precision-driven approach that delivers consistent results at every scale — from single-vehicle preparation to full national programs.',
}: ProcessSectionProps) {
  const { ref, visible } = useReveal();

  return (
    <section
      ref={ref}
      className="py-28 px-6 lg:px-12 xl:px-16 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0A0A0A 0%, #0d0c08 100%)' }}
    >
      <div className="max-w-7xl mx-auto">
        <div className={`mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-px bg-gold" />
            <span className="text-gold text-[10px] font-sans tracking-[0.35em] uppercase font-semibold">How We Work</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-serif text-white text-4xl lg:text-5xl font-bold leading-tight max-w-lg">{title}</h2>
            <p className="text-white/40 text-base font-sans leading-relaxed max-w-md lg:text-right">{subtitle}</p>
          </div>
        </div>

        {/* Steps - horizontal timeline feel */}
        <div className="relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-8 left-0 right-0 h-px bg-white/8 z-0" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <div
                key={s.step}
                className={`relative transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                {/* Step Number */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="relative z-10 w-16 h-16 flex items-center justify-center bg-[#0A0A0A] border border-gold/30">
                    <span className="font-condensed text-gold text-2xl font-bold">
                      {String(s.step).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-gold/30 to-transparent" />
                </div>

                <h3 className="font-serif text-white text-lg font-semibold mb-3 leading-snug">
                  {s.title}
                </h3>
                <p className="text-white/40 text-sm font-sans leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
